import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Loader2 } from 'lucide-react';
import { formatCurrencyFromMinor } from '@/shared/utils/money.utils';
import { formatDisplayDateTime } from '@/shared/utils/date.utils';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/lib/shadcn/components/ui/card';
import { useCustomerProductPurchasesQuery } from '../../queries/customerProductPurchases.query';
import type { CustomerProductPurchase } from '../../models/customerProductPurchase.model';
import { CustomerProductPurchaseList } from './CustomerProductPurchaseList';

export type CustomerStripeProductsSectionProps = {
  customerId: string;
};

function formatProductDisplayName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '—';

  return trimmed
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const CustomerStripeProductsSection: React.FC<
  CustomerStripeProductsSectionProps
> = ({ customerId }) => {
  const { t, i18n } = useTranslation('customers');
  const { data, isPending, isError } =
    useCustomerProductPurchasesQuery(customerId);

  const purchases = data?.data ?? [];

  const formatAmount = useCallback((purchase: CustomerProductPurchase) => {
    const currency = (purchase.Currency || 'CHF').toUpperCase();
    const value = purchase.AmountMinor / 100;
    if (!Number.isFinite(value)) return '—';

    return formatCurrencyFromMinor(purchase.AmountMinor, currency);
  }, []);

  const formatPurchasedAt = useCallback(
    (utc: string) => formatDisplayDateTime(utc, i18n.language),
    [i18n.language]
  );

  const labels = useMemo(
    () => ({
      name: t('detail.stripeProducts.name'),
      productId: t('detail.stripeProducts.productId'),
      amount: t('detail.stripeProducts.amount'),
      quantity: t('detail.stripeProducts.quantity'),
      purchasedAt: t('detail.stripeProducts.purchasedAt'),
      subscriptionFlag: t('detail.stripeProducts.subscriptionFlag'),
      invoice: t('detail.stripeProducts.viewInvoice'),
      yes: t('list.yes'),
      no: t('list.no'),
    }),
    [t]
  );

  let bodyContent: React.ReactNode;
  if (isPending) {
    bodyContent = (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        {t('detail.stripeProducts.loading')}
      </div>
    );
  } else if (isError) {
    bodyContent = (
      <p className="text-sm text-destructive py-4">
        {t('detail.stripeProducts.loadFailed')}
      </p>
    );
  } else if (purchases.length === 0) {
    bodyContent = (
      <div className="rounded-lg border border-dashed border-border/80 bg-muted/20 px-4 py-10 text-center">
        <p className="text-sm text-muted-foreground">
          {t('detail.stripeProducts.empty')}
        </p>
      </div>
    );
  } else {
    bodyContent = (
      <CustomerProductPurchaseList
        purchases={purchases}
        labels={labels}
        formatAmount={formatAmount}
        formatPurchasedAt={formatPurchasedAt}
        formatDisplayName={formatProductDisplayName}
      />
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              {t('detail.stripeProducts.sectionTitle')}
            </CardTitle>
            <p className="text-sm text-muted-foreground pt-1">
              {t('detail.stripeProducts.sectionHint')}
            </p>
          </div>
          {!isPending && !isError && purchases.length > 0 && (
            <p className="text-xs text-muted-foreground sm:pt-1">
              {t('detail.stripeProducts.totalCount', {
                count: data?.totalCount ?? purchases.length,
              })}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent>{bodyContent}</CardContent>
    </Card>
  );
};
